import React, { useState, useEffect, useCallback, useImperativeHandle } from "react";
import { Table, Card, Spinner, Alert, Button, Badge, Pagination, Modal } from "react-bootstrap";
import billingService from "../../services/billingService";
import { toast } from "sonner";
import { Download, Eye, X, Printer, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import "./InvoicesList.css";

// Invoice PDF Preview Utility - generates HTML for print preview
const generateInvoicePDFHTML = (invoice) => {
  const balanceRemaining = invoice.total_amount - invoice.amount_paid;
  const statusBgColor = {
    "PAID": "#e8f5e9",
    "PARTIAL": "#fff3e0",
    "ISSUED": "#ffebee"
  }[invoice.status] || "#f5f5f5";

  const statusTextColor = {
    "PAID": "#28a745",
    "PARTIAL": "#ff9800",
    "ISSUED": "#dc3545"
  }[invoice.status] || "#666";

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Invoice ${invoice.invoice_number}</title>
      <style>
        * { margin: 0; padding: 0; }
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: white;
          color: #333;
          line-height: 1.6;
        }
        .invoice-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 40px 20px;
          background: white;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          border-bottom: 3px solid #3b82f6;
          padding-bottom: 20px;
        }
        .school-info h1 {
          font-size: 28px;
          color: #1e3a8a;
          margin-bottom: 5px;
        }
        .school-info p {
          color: #666;
          font-size: 13px;
          margin: 2px 0;
        }
        .invoice-meta {
          text-align: right;
        }
        .invoice-meta .title {
          font-size: 20px;
          font-weight: bold;
          color: #1e3a8a;
          margin-bottom: 5px;
        }
        .invoice-meta .number {
          color: #666;
          font-size: 13px;
          margin-bottom: 10px;
        }
        .invoice-meta p {
          font-size: 13px;
          color: #666;
          margin: 3px 0;
        }
        .details-section {
          margin-bottom: 30px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }
        .details-group h4 {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          color: #1e3a8a;
          margin-bottom: 8px;
        }
        .details-group p {
          font-size: 14px;
          margin: 4px 0;
        }
        .status-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          background: ${statusBgColor};
          color: ${statusTextColor};
          margin-top: 4px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 30px 0;
        }
        th {
          background: #f1f5f9;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          border-bottom: 2px solid #e2e8f0;
          color: #1e293b;
        }
        td {
          padding: 12px;
          border-bottom: 1px solid #e2e8f0;
          font-size: 13px;
        }
        tr:last-child td {
          border-bottom: 2px solid #e2e8f0;
        }
        .amount {
          text-align: right;
          font-weight: bold;
        }
        .summary-section {
          display: flex;
          justify-content: flex-end;
          margin: 30px 0;
        }
        .summary-table {
          width: 50%;
          max-width: 400px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          font-size: 13px;
          border-bottom: 1px solid #e2e8f0;
        }
        .summary-row.total {
          font-size: 16px;
          font-weight: bold;
          color: #1e3a8a;
          padding: 15px 0;
          border-top: 2px solid #3b82f6;
          border-bottom: 2px solid #3b82f6;
          margin: 15px 0;
        }
        .payment-status {
          margin: 30px 0;
          padding: 15px;
          border-left: 4px solid #22c55e;
          background: #f0fdf4;
          font-size: 13px;
        }
        .payment-status.partial {
          background: #fffbeb;
          border-left-color: #f59e0b;
        }
        .payment-status.issued {
          background: #fee2e2;
          border-left-color: #ef4444;
        }
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          font-size: 12px;
          color: #666;
          text-align: center;
        }
        .note {
          background: #f8fafc;
          padding: 15px;
          border-radius: 4px;
          margin: 20px 0;
          font-size: 12px;
          color: #475569;
        }
        @media print {
          body { margin: 0; padding: 0; }
          .invoice-container { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <!-- Header -->
        <div class="invoice-header">
          <div class="school-info">
            <h1>School ERP Platform</h1>
            <p>Platform Subscription Invoice</p>
          </div>
          <div class="invoice-meta">
            <div class="title">INVOICE</div>
            <div class="number">#${invoice.invoice_number}</div>
            <p><strong>Date:</strong> ${new Date(invoice.issued_at).toLocaleDateString()}</p>
            <p><strong>Due Date:</strong> ${new Date(invoice.due_date).toLocaleDateString()}</p>
          </div>
        </div>

        <!-- Details -->
        <div class="details-section">
          <div class="details-group">
            <h4>Invoice Details</h4>
            <p><strong>Academic Year:</strong> ${invoice.academic_year}</p>
            <p><strong>Term:</strong> ${invoice.term.charAt(0).toUpperCase() + invoice.term.slice(1)}</p>
            <p><strong>Student Count:</strong> ${invoice.student_count}</p>
          </div>
          <div class="details-group">
            <h4>Status</h4>
            <div class="status-badge">${invoice.status}</div>
          </div>
        </div>

        <!-- Items -->
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Student Count</th>
              <th>Unit Price</th>
              <th class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Platform Subscription Fee</td>
              <td>${invoice.student_count}</td>
              <td>GHS ${invoice.unit_price?.toFixed(2) || '0.00'}</td>
              <td class="amount">GHS ${invoice.total_amount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <!-- Summary -->
        <div class="summary-section">
          <div class="summary-table">
            <div class="summary-row">
              <div>Total Amount:</div>
              <div>GHS ${invoice.total_amount.toFixed(2)}</div>
            </div>
            <div class="summary-row">
              <div>Amount Paid:</div>
              <div>GHS ${invoice.amount_paid.toFixed(2)}</div>
            </div>
            <div class="summary-row total">
              <div>Outstanding Balance:</div>
              <div style="color: ${balanceRemaining > 0 ? '#ef4444' : '#22c55e'}">
                GHS ${Math.max(0, balanceRemaining).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Status -->
        ${invoice.status === 'PAID' ? `
          <div class="payment-status">
            <strong>✓ Payment Status: FULLY PAID</strong><br>
            Thank you for your payment. This invoice has been settled in full.
            ${invoice.paid_at ? `<br>Payment received on: ${new Date(invoice.paid_at).toLocaleDateString()}` : ''}
          </div>
        ` : invoice.status === 'PARTIAL' ? `
          <div class="payment-status partial">
            <strong>⚠ Payment Status: PARTIALLY PAID</strong><br>
            Outstanding balance of GHS ${balanceRemaining.toFixed(2)} remains due. Please settle by ${new Date(invoice.due_date).toLocaleDateString()}.
          </div>
        ` : `
          <div class="payment-status issued">
            <strong>⚠ Payment Status: OUTSTANDING</strong><br>
            Payment of GHS ${invoice.total_amount.toFixed(2)} is due by ${new Date(invoice.due_date).toLocaleDateString()}.
          </div>
        `}

        <!-- Note -->
        <div class="note">
          <strong>Payment Instructions:</strong><br>
          Please remit payment through our secure payment portal using Mobile Money (MTN, Vodafone, AirtelTigo) 
          or Debit/Credit Card. Reference your invoice number ${invoice.invoice_number} when making payment.
        </div>

        <!-- Footer -->
        <div class="footer">
          <p><strong>School ERP Platform</strong></p>
          <p>For payment inquiries: info@campusio.online | +233 53 448 4781</p>
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return htmlContent;
};

// Function to preview invoice in print window
const previewInvoicePDF = (invoice) => {
  try {
    const printWindow = window.open("", "", "width=900,height=1200");
    const htmlContent = generateInvoicePDFHTML(invoice);
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Small delay to ensure content is loaded before printing
    setTimeout(() => {
      printWindow.print();
    }, 250);
  } catch (error) {
    console.error("Error previewing invoice:", error);
    toast.error("Failed to preview invoice");
  }
};

/**
 * Invoices List Component
 * Displays school's subscription invoices and payment history
 */
const InvoicesList = React.forwardRef((props, ref) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const itemsPerPage = 10;

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const data = await billingService.getInvoices(itemsPerPage, offset);
      setInvoices(data.data || []);
      setTotalInvoices(data.count || 0);
    } catch (err) {
      console.error("Error loading invoices:", err);
      setError(err.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: loadInvoices
  }));

  useEffect(() => {
    loadInvoices();
  }, [currentPage, loadInvoices]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIAL":
        return "warning";
      case "ISSUED":
        return "danger";
      case "DRAFT":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      setDownloadingId(invoice.id);
      const response = await billingService.downloadInvoice(invoice.id);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const totalPages = Math.ceil(totalInvoices / itemsPerPage);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" /> Loading invoices...
      </div>
    );
  }

  return (
    <div className="invoices-list">
      <h2 className="mb-4">Invoice History</h2>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {invoices.length === 0 ? (
        <Alert variant="info">
          <strong>No invoices found.</strong> Your invoices will appear here once billing is enabled.
        </Alert>
      ) : (
        <>
          <Card className="shadow-sm mb-4">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Invoice Number</th>
                      <th>Academic Term</th>
                      <th>Student Count</th>
                      <th>Total Amount</th>
                      <th>Amount Paid</th>
                      <th>Status</th>
                      <th>Issued Date</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="invoice-row">
                        <td className="fw-bold">{invoice.invoice_number}</td>
                        <td>
                          {invoice.academic_year} - {invoice.term.charAt(0).toUpperCase() + invoice.term.slice(1)}
                        </td>
                        <td>{invoice.student_count}</td>
                        <td className="text-danger fw-bold">
                          GHS {invoice.total_amount.toFixed(2)}
                        </td>
                        <td className="text-success">
                          GHS {invoice.amount_paid.toFixed(2)}
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeClass(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </td>
                        <td>{formatDate(invoice.issued_at)}</td>
                        <td className="fw-bold">{formatDate(invoice.due_date)}</td>
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(invoice)}
                              title="View invoice details"
                              className="me-2"
                            >
                              <Eye size={16} weight="fill" />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => previewInvoicePDF(invoice)}
                              title="Preview and print invoice"
                              className="me-2"
                            >
                              <Printer size={16} weight="fill" />
                            </Button>
                            
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mb-4">
              <Pagination>
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />

                {Array.from({ length: totalPages }, (_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}

                <Pagination.Next
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}

          {/* Summary Card */}
          <Card className="shadow-sm border-0">
            <Card.Body>
              <div className="row">
                <div className="col-md-4">
                  <h6 className="text-muted mb-2">Total Invoices</h6>
                  <h4 className="mb-0">{totalInvoices}</h4>
                </div>
                <div className="col-md-4">
                  <h6 className="text-muted mb-2">Total Issued</h6>
                  <h4 className="mb-0 text-danger">
                    GHS{" "}
                    {invoices
                      .reduce((sum, inv) => sum + inv.total_amount, 0)
                      .toFixed(2)}
                  </h4>
                </div>
                <div className="col-md-4">
                  <h6 className="text-muted mb-2">Total Paid</h6>
                  <h4 className="mb-0 text-success">
                    GHS{" "}
                    {invoices
                      .reduce((sum, inv) => sum + inv.amount_paid, 0)
                      .toFixed(2)}
                  </h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal 
          invoice={selectedInvoice}
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          onPreview={() => previewInvoicePDF(selectedInvoice)}
          onDownload={() => handleDownloadInvoice(selectedInvoice)}
          isDownloading={downloadingId === selectedInvoice.id}
        />
      )}
    </div>
  );
});

InvoicesList.displayName = "InvoicesList";

// Invoice Detail Modal Component
const InvoiceDetailModal = ({ invoice, show, onHide, onPreview, onDownload, isDownloading }) => {
  const balanceRemaining = invoice.total_amount - invoice.amount_paid;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="fw-bold">
          Invoice #{invoice.invoice_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="invoice-detail">
          {/* Header Info */}
          <div className="row mb-4">
            <div className="col-md-6">
              <h6 className="text-muted">Academic Term</h6>
              <p className="mb-3">
                {invoice.academic_year} - {invoice.term.charAt(0).toUpperCase() + invoice.term.slice(1)}
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted">Status</h6>
              <Badge 
                bg={
                  invoice.status === "PAID" ? "success" : 
                  invoice.status === "PARTIAL" ? "warning" : 
                  invoice.status === "ISSUED" ? "danger" : "secondary"
                }
              >
                {invoice.status}
              </Badge>
            </div>
          </div>

          {/* Dates */}
          <div className="row mb-4">
            <div className="col-md-6">
              <h6 className="text-muted">Issued Date</h6>
              <p className="mb-3">
                {new Date(invoice.issued_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </p>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted">Due Date</h6>
              <p className="mb-3">
                <strong className={new Date(invoice.due_date) < new Date() ? "text-danger" : ""}>
                  {new Date(invoice.due_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </strong>
              </p>
            </div>
          </div>

          {/* Billing Details */}
          <div className="card bg-light mb-4">
            <div className="card-body">
              <h6 className="card-title fw-bold mb-3">Billing Summary</h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2">
                    <span className="text-muted">Student Count:</span>
                    <span className="float-end fw-bold">{invoice.student_count}</span>
                  </p>
                  <p className="mb-2">
                    <span className="text-muted">Unit Price:</span>
                    <span className="float-end fw-bold">GHS {invoice.unit_price?.toFixed(2) || '0.00'}</span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <span className="text-muted">Total Amount:</span>
                    <span className="float-end fw-bold text-danger">GHS {invoice.total_amount.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Tracking */}
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title fw-bold mb-3">Payment Tracking</h6>
              <div className="row">
                <div className="col-md-4">
                  <p className="mb-0">
                    <span className="text-muted d-block" style={{ fontSize: "12px" }}>Amount Paid</span>
                    <span className="fw-bold text-success" style={{ fontSize: "18px" }}>
                      GHS {invoice.amount_paid.toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="mb-0">
                    <span className="text-muted d-block" style={{ fontSize: "12px" }}>Total Amount</span>
                    <span className="fw-bold text-primary" style={{ fontSize: "18px" }}>
                      GHS {invoice.total_amount.toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="mb-0">
                    <span className="text-muted d-block" style={{ fontSize: "12px" }}>Outstanding</span>
                    <span className={`fw-bold ${balanceRemaining > 0 ? "text-danger" : "text-success"}`} style={{ fontSize: "18px" }}>
                      GHS {balanceRemaining.toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status Message */}
          {invoice.status === "PAID" && (
            <Alert variant="success" className="mt-3 mb-0">
              <CheckCircle size={18} className="me-2" />
              This invoice has been fully paid. Thank you!
            </Alert>
          )}
          {invoice.status === "PARTIAL" && (
            <Alert variant="warning" className="mt-3 mb-0">
              <WarningCircle size={18} className="me-2" />
              Outstanding balance of GHS {balanceRemaining.toFixed(2)} is remaining.
            </Alert>
          )}
          {invoice.status === "ISSUED" && (
            <Alert variant="danger" className="mt-3 mb-0">
              <WarningCircle size={18} className="me-2" />
              Full payment of GHS {invoice.total_amount.toFixed(2)} is due.
            </Alert>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={onPreview}
          className="me-2"
        >
          <Printer size={18} weight="fill" className="me-2" />
          Preview & Print
        </Button>
        
      </Modal.Footer>
    </Modal>
  );
};

export default InvoicesList;
